/**
 * Card tiers and CustomCard configuration schemas.
 * Standardized 5-tier card architecture: chip, compact, medium, full, custom.
 */

export type ItemCardTier = "chip" | "compact" | "medium" | "full" | "custom";

export type CardEntityType = "contact" | "group" | "memory" | "media" | "tag" | "share" | "user" | "message" | "place" | "anchor";

/**
 * Standard linked section keys supported on Custom, Medium, and Full cards.
 * Note: Linked entities (Contacts, Groups, Memories) are unified into the canonical "links" section.
 */
export type CustomCardSectionKey =
  | "media"
  | "links"
  | "tags"
  | "shares"
  | "sharedItem";

/**
 * User-defined layout configuration for a specific entity type in a Custom Card.
 */
export interface ICustomCardEntityConfig {
  /** The specific fields to display, in order */
  fields: string[];
  /** The specific linked sections to display, in order (e.g. "links", "media", "tags", "shares") */
  sections: string[];
}

/**
 * Embedded JSON Block: CustomCard
 * Stored in Firestore collection `config/{userId}` under key `CustomCard`.
 */
export interface ICustomCardConfig {
  contact?: ICustomCardEntityConfig;
  group?: ICustomCardEntityConfig;
  memory?: ICustomCardEntityConfig;
  media?: ICustomCardEntityConfig;
  tag?: ICustomCardEntityConfig;
  share?: ICustomCardEntityConfig;
  user?: ICustomCardEntityConfig;
  message?: ICustomCardEntityConfig;
  place?: ICustomCardEntityConfig;
  anchor?: ICustomCardEntityConfig;
}

/**
 * Document schema for the `config` collection (`config/{userId}`).
 */
export interface IUserConfigDocument {
  userId: string;
  CustomCard?: ICustomCardConfig;
  updatedAt?: number;
}

/**
 * Default custom card configuration for newly provisioned users.
 * Uses the "Compact" definition as specified by the platform standards.
 */
export const DEFAULT_CUSTOM_CARD_CONFIG: Required<ICustomCardConfig> = {
  contact: {
    fields: ["displayName", "photo"],
    sections: [],
  },
  group: {
    fields: ["name", "photo"],
    sections: [],
  },
  memory: {
    fields: ["title", "date", "photo"],
    sections: [],
  },
  media: {
    fields: ["thumbnail"],
    sections: [],
  },
  tag: {
    fields: ["name", "icon"],
    sections: [],
  },
  share: {
    fields: ["sharedItem", "recipientName"],
    sections: [],
  },
  user: {
    fields: ["displayName", "email", "photo"],
    sections: [],
  },
  message: {
    fields: ["subject", "sender", "recipient", "date"],
    sections: [],
  },
  place: {
    fields: ["name", "address", "photo"],
    sections: [],
  },
  anchor: {
    fields: ["name", "type", "photo"],
    sections: [],
  },
};

/**
 * Resolves the effective custom card configuration for an entity type,
 * falling back to the default compact configuration if not defined.
 *
 * @param config - The user's stored custom card configuration.
 * @param entityType - The entity domain type.
 * @returns Resolved entity card configuration.
 */
export function resolveCustomCardEntityConfig(
  config: ICustomCardConfig | undefined | null,
  entityType: CardEntityType
): ICustomCardEntityConfig {
  return config?.[entityType] ?? DEFAULT_CUSTOM_CARD_CONFIG[entityType];
}
